"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { CircleCheck, Loader, ArrowRight, Building2, Calendar, Mail, Phone, User, FileText, MessageCircle } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function DemoFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="8216" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="rounded-full bg-[var(--color-secondary)]/20 p-4 ring-1 ring-[var(--color-secondary)]/50">
          <CircleCheck className="size-12 text-[var(--color-secondary)]" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-white">¡Inscripción Recibida!</h3>
          <p className="text-slate-300 max-w-xs mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-white/20 text-white hover:bg-white/10 hover:text-white rounded-xl">
          Enviar otra consulta
        </Button>
      </div>;
  }
  const inputClasses = "flex h-10 w-full border-b border-white/20 bg-transparent px-0 py-2 text-sm text-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:border-[var(--color-secondary)] focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-300 font-sans";
  const labelClasses = "text-slate-300 text-xs uppercase tracking-widest font-bold font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "El nombre es obligatorio"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Nombre Completo</FormLabel>
              <FormControl>
                <div className="relative">
                  <input {...field} placeholder="Tu nombre y apellido" className={inputClasses} />
                  <User className="absolute right-0 top-2.5 size-4 text-slate-500 pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "El correo es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ingresa un correo electrónico válido"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Correo Electrónico</FormLabel>
              <FormControl>
                <div className="relative">
                  <input {...field} type="email" placeholder="ejemplo@correo.com" className={inputClasses} />
                  <Mail className="absolute right-0 top-2.5 size-4 text-slate-500 pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="company_name" rules={{
        required: "El curso es obligatorio"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Curso de Interés</FormLabel>
              <FormControl>
                <div className="relative">
                  <input {...field} placeholder="Ej: Barbería, Electricista..." className={inputClasses} />
                  <Building2 className="absolute right-0 top-2.5 size-4 text-slate-500 pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Teléfono <span className="text-slate-600 normal-case ml-1">(WhatsApp)</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <input {...field} type="tel" placeholder="09X XXX XXX" className={inputClasses} />
                  <Phone className="absolute right-0 top-2.5 size-4 text-slate-500 pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="preferred_demo_date" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelClasses}>Fecha Estimada de Inicio <span className="text-slate-600 normal-case ml-1">(Opcional)</span></FormLabel>
            <FormControl>
              <div className="relative">
                <input {...field} type="date" className={`${inputClasses} [color-scheme:dark]`} />
                <SafeHydrate><Calendar className="absolute right-0 top-2.5 size-4 text-slate-500 pointer-events-none" /></SafeHydrate>
              </div>
            </FormControl>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      {}
      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelClasses}>Consultas Adicionales <span className="text-slate-600 normal-case ml-1">(Opcional)</span></FormLabel>
            <FormControl>
              <div className="relative">
                <textarea {...field} placeholder="Contanos tus dudas o qué esperas del curso..." className={`${inputClasses} min-h-[80px] resize-y`} />
                <FileText className="absolute right-0 top-2.5 size-4 text-slate-500 pointer-events-none" />
              </div>
            </FormControl>
            <FormMessage className="text-red-400" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-gradient-to-r from-[var(--color-secondary)] to-amber-600 hover:to-amber-500 text-[var(--color-secondary-foreground)] font-bold text-lg rounded-xl shadow-[0_0_20px_-5px_var(--color-secondary)] hover:shadow-[0_0_25px_-5px_var(--color-secondary)] transition-all duration-300 border-0 uppercase tracking-wider">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Procesando...
          </> : <>
            Inscribirme Ahora
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>

      <Link to="https://mpago.la/1WfMEZk" className="w-full block" newTab>
        <Button type="button" className="w-full h-14 bg-[#009EE3] hover:bg-[#008ED2] text-white font-bold text-lg rounded-xl shadow-lg transition-all duration-300 border-0 uppercase tracking-wider">
          Pagar con Mercado Pago
          <ArrowRight className="ml-2 size-5" />
        </Button>
      </Link>

      <Link to="https://wa.me/59896344395" className="w-full block" newTab>
        <Button type="button" className="w-full h-14 bg-[#25D366] hover:bg-[#22c35e] text-white font-bold text-lg rounded-xl shadow-lg transition-all duration-300 border-0 uppercase tracking-wider">
          <MessageCircle className="mr-2 size-5" />
          Consultar por WhatsApp
        </Button>
      </Link>
      
      <p className="text-xs text-center text-slate-500 mt-4 font-sans">
        Al enviar este formulario, aceptas nuestras <Link to="#" className="underline hover:text-slate-300 transition-colors">Políticas de Privacidad</Link>.
      </p>
    </Form>;
}
export default function RequestADemo() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="inscripción" className="relative w-full py-24 overflow-hidden bg-[hsl(222,47%,11%)] text-foreground">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[hsl(222,47%,11%)]/85 z-10 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(222,47%,11%)] via-transparent to-[hsl(222,47%,11%)] z-10" />
        <div className="w-full h-full bg-cover bg-center bg-no-repeat opacity-30 grayscale contrast-125 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583410/img/abstract_architecture_navy.png)]" />
      </div>

      <div className="container relative z-20 mx-auto px-4 md:px-6">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-5 space-y-8 text-center lg:text-left">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white font-sans uppercase">
                Inscribite hoy y comenzá tu <span className="text-[var(--color-secondary)]">propio negocio</span>
              </h2>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed font-sans">
                Formate con los mejores profesionales del interior. Cursos técnicos, de estética y salud con rápida salida laboral en todo Uruguay.
              </p>
            </div>

            <div className="hidden lg:grid gap-6">
              {[{
              title: "Capacitación Práctica",
              desc: "Clases dinámicas enfocadas en el aprendizaje real."
            }, {
              title: "Docentes Expertos",
              desc: "Profesionales con years de experiencia en su rubro."
            }, {
              title: "Certificación Nacional",
              desc: "Título con respaldo de ECAP para todo el país."
            }].map((item, i) => <div key={i} data-index={i} className="flex gap-4 items-start group">
                  <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)]/20 text-[var(--color-primary)] ring-1 ring-[var(--color-primary)]/50 group-hover:bg-[var(--color-primary)] group-hover:text-white transition-colors duration-300 shadow-lg">
                    <CircleCheck className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-lg font-sans">{item.title}</h3>
                    <p className="text-sm text-slate-400 font-sans">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="border-0 bg-[hsl(222,47%,15%)]/70 backdrop-blur-xl shadow-2xl ring-1 ring-white/10 overflow-hidden rounded-3xl">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-[var(--color-secondary)] to-transparent opacity-80" />
              
              <CardHeader className="pb-2 border-b border-white/5">
                <CardTitle className="text-3xl font-extrabold text-white font-sans">Formulario de Inscripción</CardTitle>
                <CardDescription className="text-slate-400 text-base font-sans">
                  Completá tus datos y un asesor se pondrá en contacto a la brevedad.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-8">
                <FormProvider formId="inscripcion_ecap_digital_academy" sectionName="inscripcion-section" defaultValues={{
                full_name: "",
                email: "",
                company_name: "",
                phone: "",
                preferred_demo_date: "",
                additional_details: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                company_name: {
                  required: true
                }
              }} submitText="Inscribirme Ahora" successMessage="¡Gracias! Tu solicitud ha sido recibida correctamente. Un asesor de ECAP se comunicará contigo pronto para confirmar los detalles." errorMessage="No pudimos enviar tu solicitud. Por favor, verifica tu conexión e intenta de nuevo." className="w-full">
                  <DemoFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}